import { PLATFORM_ID, inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpErrorResponse,
  HttpEvent
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { isPlatformServer } from '@angular/common';
import { Router } from '@angular/router';
import { LocalStorageService } from '../storage/local-storage.service';
import { AuthenticateService } from 'src/app/modules/authenticate/services/authenticate.service';
import { StateService } from '../storage/state.service';
import {
  AUTH_RETRY_CONTEXT,
  CLEAR_CACHE_CONTEXT
} from '../http-cache/http-cache.model';
import {
  getHttpRequestDedupeKey,
  invalidateHttpCacheForRequest
} from '../http-cache/http-cache.service';
import { LocalStorageVariables } from '../../enum/localstorage-variables';
import { ApiUrls } from '../../enum/routes';

function shouldTryTokenRefresh(req: HttpRequest<unknown>, error: HttpErrorResponse) {
  if (req.context.get(AUTH_RETRY_CONTEXT)) {
    return false;
  }
  const isRefreshEndpoint = req.url.includes('/refresh-token');
  const code = String(error.error?.code ?? '');
  const hasRefreshErrorCode = code === '4041' || code === '4177';
  return !isRefreshEndpoint && (error.status === 401 || hasRefreshErrorCode);
}

function isProfileRequest(req: HttpRequest<unknown>): boolean {
  return req.url.includes(ApiUrls.profile);
}

function hasInvalidRefreshTokenCode(error: HttpErrorResponse): boolean {
  const code = String(error.error?.code ?? '');
  return code === '4041' || code === '4177';
}

/** One retry per request fingerprint while several callers share the same 401 refresh. */
const authRetryInFlight = new Map<string, Observable<HttpEvent<unknown>>>();

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthenticateService);
  const state = inject(StateService);
  const storage = inject(LocalStorageService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  const isServer = isPlatformServer(platformId);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isServer) {
        return throwError(() => error);
      }

      if (!shouldTryTokenRefresh(req, error)) {
        return throwError(() => error);
      }

      const refreshToken =
        state.refreshToken() ??
        storage.getItem(LocalStorageVariables.refresh_token);

      if (!refreshToken) {
        if (!isProfileRequest(req)) {
          hardLogout();
        }
        return throwError(() => error);
      }

      const dedupeKey = getHttpRequestDedupeKey(req);

      return authService.refreshTokenShared(refreshToken).pipe(
        switchMap((res) => {
          state.setAuthTokens(res.token, res.refresh_token);
          state.setLoginStatus(true);

          storage.setItem(LocalStorageVariables.access_token, res.token);
          storage.setItem(LocalStorageVariables.refresh_token, res.refresh_token);

          invalidateHttpCacheForRequest(req);

          if (!authRetryInFlight.has(dedupeKey)) {
            const retryReq = req.clone({
              context: req.context
                .set(CLEAR_CACHE_CONTEXT, true)
                .set(AUTH_RETRY_CONTEXT, true)
            });

            authRetryInFlight.set(
              dedupeKey,
              next(retryReq).pipe(
                finalize(() => authRetryInFlight.delete(dedupeKey))
              )
            );
          }

          return authRetryInFlight.get(dedupeKey)!;
        }),
        catchError((refreshError: HttpErrorResponse) => {
          authRetryInFlight.delete(dedupeKey);
          if (hasInvalidRefreshTokenCode(refreshError)) {
            hardLogout();
          } else if (isProfileRequest(req)) {
            state.setUserProfile(null);
          }
          return throwError(() => refreshError);
        })
      );

      function hardLogout() {
        state.clearAuthTokens();
        storage.logOutUser();

        const currentUrl = router.url ?? '';

        if (currentUrl.includes('/profile')) {
          router.navigate(['authenticate/login']);
        }
      }
    })
  );
};
